import React, { useState } from 'react';
import { Input, Table, Tag, Button, Modal, message } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import { inspectionApi } from '../../services/api';
import * as XLSX from 'xlsx';
import ProductDetailModal from './ProductDetail';

const Dashboard = () => {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  // 详情弹窗
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailProductId, setDetailProductId] = useState(null);

  // 导出loading
  const [exporting, setExporting] = useState(false);

  const handleSearch = async (page = 1) => {
    if (!keyword.trim()) {
      message.warning('请输入搜索关键词');
      return;
    }
    setLoading(true);
    try {
      const res = await inspectionApi.search({ keyword: keyword.trim(), page, limit: pagination.pageSize });
      setData(res.data.list || []);
      setPagination({ ...pagination, current: page, total: res.data.pagination.total });
    } catch (err) {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  const showDetail = (id) => {
    setDetailProductId(id);
    setDetailVisible(true);
  };

  const handleExport = async () => {
    if (!keyword.trim()) {
      message.warning('请先搜索再导出');
      return;
    }
    setExporting(true);
    try {
      const res = await inspectionApi.exportSearch({ keyword: keyword.trim() });
      const exportData = res.data || [];
      if (exportData.length === 0) {
        message.warning('没有可导出的数据');
        return;
      }

      // 列名映射
      const columnMap = {
        product_code: '货品编码',
        name: '货品名称',
        batch_category: '品类',
        material: '材质',
        weight: '重量(g)',
        dimensions: '尺寸',
        crafts_man: '工艺师',
        finish_date: '完工日期',
        company_name: '所属公司',
        raw_material_origin: '原石产地',
        jade_type_name: '料种',
        '原石照片URL': '原石照片',
        '原石入库照片URL': '原石入库照片',
        '切料照片URL': '切料照片',
        '切料标块照片URL': '切料标块照片',
        '规划照片URL': '规划照片',
        '手修照片URL': '手修照片',
        '抛光照片URL': '抛光照片',
        '打柱照片URL': '打柱照片',
        '磨珠照片URL': '磨珠照片',
        '穿珠子照片URL': '穿珠子照片',
        '成品照片URL': '成品照片',
        '检测证书URL': '检测证书',
      };

      // 转换数据
      const rows = exportData.map(item => {
        const row = {};
        Object.entries(columnMap).forEach(([key, label]) => {
          row[label] = item[key] || '';
        });
        return row;
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '货品资料');
      const filename = `国检验证_${keyword.trim()}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      message.success(`已导出 ${exportData.length} 条数据`);
    } catch (err) {
      message.error('导出失败');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: '货品编码',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (text, record) => (
        <a onClick={() => showDetail(record.id)} style={{ color: '#1890ff' }}>{text}</a>
      )
    },
    {
      title: '货品名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '品类',
      dataIndex: 'batch_category',
      key: 'batch_category',
      render: (cat) => cat ? <Tag color="#1890ff">{cat}</Tag> : '-'
    },
    {
      title: '材质',
      dataIndex: 'material',
      key: 'material',
    },
    {
      title: '重量(g)',
      dataIndex: 'weight',
      key: 'weight',
    },
    {
      title: '工艺师',
      dataIndex: 'crafts_man',
      key: 'crafts_man',
    },
    {
      title: '所属公司',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text) => <Tag>{text}</Tag>
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Input.Search
          placeholder="输入货品编码、名称或批次编号搜索"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={() => handleSearch(1)}
          onPressEnter={() => handleSearch(1)}
          enterButton={<Button type="primary" icon={<SearchOutlined />}>搜索</Button>}
          size="large"
          style={{ maxWidth: 600 }}
          allowClear
        />
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={exporting}
          disabled={pagination.total === 0}
          size="large"
        >
          导出Excel
        </Button>
      </div>
      <div style={{ marginBottom: 12, color: '#999', fontSize: 13 }}>
        支持模糊搜索，如输入YT可匹配YT-1, YT-2等
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 件`,
          onChange: (page) => handleSearch(page),
        }}
      />

      <Modal
        title="货品详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <ProductDetailModal productId={detailProductId} visible={detailVisible} />
      </Modal>
    </div>
  );
};

export default Dashboard;
